
import { supabase } from "@/integrations/supabase/client";
import { handleError } from "@/utils/error-handling";
import { AnalyticsData, GameSession, SOSEvent, PerformanceMetricRecord } from "./types";

/**
 * Get analytics data for dashboard between specified dates
 * @param startDate Start date for analytics data range
 * @param endDate End date for analytics data range
 * @returns Promise resolving to analytics data or null if unsuccessful
 */
export const getAnalyticsData = async (
  startDate: Date, 
  endDate: Date
): Promise<AnalyticsData | null> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      handleError(userError, "Failed to authenticate user");
      return null;
    }
    
    const userId = userData?.user?.id;
    
    if (!userId) {
      console.log('No authenticated user found');
      return null;
    }
    
    // Format dates for query
    const startDateStr = startDate.toISOString();
    const endDateStr = endDate.toISOString();
    
    // Get game sessions with proper error handling
    const { data: sessionData, error: sessionError } = await supabase
      .from('game_sessions')
      .select('*')
      .eq('user_id', userId)
      .gte('started_at', startDateStr)
      .lte('started_at', endDateStr);
      
    if (sessionError) {
      console.error('Error fetching game sessions:', sessionError);
      handleError(sessionError, "Failed to fetch game session analytics");
      return null;
    }
    
    // Get SOS events with proper error handling
    const { data: sosData, error: sosError } = await supabase
      .from('sos_events')
      .select('*')
      .eq('user_id', userId)
      .gte('created_at', startDateStr)
      .lte('created_at', endDateStr);
      
    if (sosError) {
      console.error('Error fetching SOS events:', sosError);
      handleError(sosError, "Failed to fetch SOS event analytics");
      return null;
    }
    
    // Get performance metrics with proper error handling
    const { data: metricsData, error: metricsError } = await supabase
      .from('analytics_events' as any)
      .select('*')
      .eq('user_id', userId)
      .eq('event_type', 'performance_metric')
      .gte('timestamp', startDateStr)
      .lte('timestamp', endDateStr);
      
    if (metricsError) {
      console.error('Error fetching performance metrics:', metricsError);
      handleError(metricsError, "Failed to fetch performance metrics");
      return null;
    }
    
    // Process and return the data with proper type casting
    // First ensure the data is safe to cast by using a type guard
    return {
      gameSessions: (sessionData || []) as GameSession[],
      sosEvents: (sosData || []) as SOSEvent[],
      performanceMetrics: (metricsData ? metricsData as unknown as PerformanceMetricRecord[] : [])
    };
  } catch (error) {
    console.error('Error in getAnalyticsData:', error);
    handleError(error, "An unexpected error occurred while fetching analytics data");
    return null;
  }
};
